import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { holidayDays, plainDateFromIso, type HolidaySource, type Messages } from '@repere/core';
import type { CSSProperties, TouchEvent } from 'react';
import { Alerte, Bouton, Invisible } from '../ui';
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';
import { messageDErreur, useSession } from '../auth/session';
import { maintenant } from '../horloge';
import { cleDuJour, dateComplete, grilleDuMois, moisEtAnnee, nomsJoursCourts } from './dates';
import { useTraduction } from '../i18n/langue';
import {
  cleSourceConges,
  teinteEnfant,
  type SourceCongesView,
  type SourceEnVigueur,
} from '../compte/SourceConges';
import type { Occurrence, ParentDuDossier } from './types';

/**
 * Vue mois du calendrier de garde.
 *
 * Les deux parents sont distingués par la couleur **et** par un libellé lu par les
 * lecteurs d'écran : une information portée par la seule teinte serait inaccessible,
 * et sur ce produit l'information « qui a l'enfant » ne souffre pas d'ambiguïté.
 */
/** « Échange de jours » ou « Dérogation convenue », selon ce qui l'a produite. */
const libelleDerogation = (periode: Occurrence | undefined, m: Messages['dossier']['calendrier']): string =>
  periode?.origin === 'day_swap' ? m.libelleEchange : m.libelleDerogation;

/**
 * L'infobulle du marqueur : ce qui s'est passé, et pourquoi si un motif a été donné.
 *
 * Une dérogation sans motif reste sans motif — l'écran ne comble pas le silence par une
 * formule. Sur un calendrier que deux parents relisent, une explication inventée serait
 * pire que pas d'explication du tout.
 */
const titreDerogation = (periode: Occurrence | undefined, m: Messages['dossier']['calendrier']): string => {
  const quoi = periode?.origin === 'day_swap' ? m.titreEchange : m.titreDerogationConvenue;
  return periode?.reason ? m.avecMotif(quoi, periode.reason) : quoi;
};

/**
 * Ce que le serveur rend, ramené à ce que le noyau sait peindre.
 *
 * Le calendrier ne connaît pas la liste des sources — il ne connaît que `kind`. Une source
 * de plus (un calendrier belge, l'abonnement à une URL) se branche ici et nulle part
 * ailleurs dans cet écran.
 */
const sourceDesConges = (vue: SourceEnVigueur): HolidaySource =>
  vue.kind === 'ics'
    ? { kind: 'ics', periods: vue.periods ?? [] }
    : { kind: 'education_gouv', zone: vue.zone };

/** Vrai si cette source a quelque chose à peindre — sinon ni contour, ni légende. */
const sourceUtile = (vue: SourceEnVigueur): boolean =>
  vue.kind === 'ics' ? (vue.periods?.length ?? 0) > 0 : vue.zone !== null;

/**
 * Un triangle plein, jamais un chevron au trait : c'est la forme de tout le langage
 * graphique du produit (`Picto.tsx`), reprise ici pour deux flèches qui n'ont pas leur
 * place dans les pictogrammes de catégorie. `currentColor` plutôt qu'une teinte fixe : le
 * bouton qui la porte sait déjà de quelle couleur parler, en clair comme en sombre.
 */
function FlecheMois({ sens }: { sens: 'precedent' | 'suivant' }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false">
      <path d={sens === 'precedent' ? 'M7 1 L7 9 L2 5 Z' : 'M3 1 L3 9 L8 5 Z'} fill="currentColor" />
    </svg>
  );
}

/** Distance minimale, en pixels, avant qu'un geste horizontal ne compte comme un balayage. */
const SEUIL_BALAYAGE = 40;

/**
 * Une couche de congés : des jours à encadrer, une teinte, un libellé de légende.
 *
 * Le dossier en produit **une**, la fratrie séparée **une par enfant**. La grille ne
 * distingue pas les deux cas : elle empile ce qu'on lui donne, et c'est ce qui permet à
 * l'option « les enfants ont les mêmes congés » de ne toucher à rien d'autre.
 */
interface CoucheConges {
  cle: string;
  /** Nul pour la couche du dossier : elle garde le contour unique de la charte. */
  teinte: string | null;
  /** Le prénom de l'enfant, dit en toutes lettres dans la case. Nul pour le dossier. */
  prenom: string | null;
  legende: string;
  jours: Set<string>;
}

/**
 * Les couches à peindre pour le mois affiché.
 *
 * Aucune quand la source ne donne rien : le produit se tait plutôt que d'afficher les
 * vacances de quelqu'un d'autre.
 */
function couchesDeConges(
  vue: SourceCongesView | undefined,
  m: Messages['dossier']['calendrier'],
  debut: string,
  fin: string,
): CoucheConges[] {
  if (!vue) return [];
  const jours = (source: SourceEnVigueur): Set<string> =>
    holidayDays(sourceDesConges(source), plainDateFromIso(debut), plainDateFromIso(fin));
  const libelle = (source: SourceEnVigueur): string =>
    source.kind === 'ics' ? m.legendeCongeFichier : m.legendeConge(source.zone ?? '');

  if (vue.shared) {
    return sourceUtile(vue.dossier)
      ? [
          {
            cle: 'dossier',
            teinte: null,
            prenom: null,
            legende: libelle(vue.dossier),
            jours: jours(vue.dossier),
          },
        ]
      : [];
  }

  return vue.children.filter(sourceUtile).map((enfant) => ({
    cle: enfant.childId,
    // Sa couleur d'avatar, celle sous laquelle il apparaît partout ailleurs. En inventer
    // une seconde pour le calendrier demanderait de retenir deux codes par enfant.
    teinte: teinteEnfant(enfant.color),
    prenom: enfant.firstName,
    legende: m.legendeCongeEnfant(enfant.firstName, libelle(enfant)),
    jours: jours(enfant),
  }));
}

/**
 * Les contours empilés d'une case, du plus intérieur au plus extérieur.
 *
 * Trois au plus : au-delà, les anneaux mangent le chiffre du jour et ne se distinguent plus
 * les uns des autres. Les prénoms, eux, restent tous dans le texte invisible — c'est là que
 * l'information est complète, pas dans la peinture.
 */
const contoursConges = (couches: CoucheConges[]): CSSProperties | undefined => {
  const teintes = couches.map((couche) => couche.teinte).filter((teinte) => teinte !== null);
  if (teintes.length === 0) return undefined;
  return {
    boxShadow: teintes
      .slice(0, 3)
      .map((teinte, rang) => `inset 0 0 0 ${2 * (rang + 1)}px ${teinte}`)
      .join(', '),
  };
};

export function Calendrier({
  caseFileId,
  parents,
  moisInitial,
}: {
  caseFileId: string;
  parents: ParentDuDossier[];
  moisInitial?: Date;
}) {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const m = messages.dossier.calendrier;
  const [curseur, setCurseur] = useState(() => {
    const base = moisInitial ?? maintenant();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const rail = useRequeteMedia(ECRAN_LARGE);

  const changerDeMois = (delta: number) =>
    setCurseur((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));

  /**
   * Le balayage tactile change de mois.
   *
   * **Il est seul à le faire sur téléphone**, depuis que la barre de mois ne se rend plus
   * que sur le rail : ce commentaire disait l'inverse, et il faut que la limite soit
   * écrite là où elle se lit. Un geste sans commande visible n'est annoncé à personne — ni
   * au clavier, ni au lecteur d'écran, qui capte lui-même les balayages. Sous 860 px,
   * changer de mois n'a donc pas d'équivalent non tactile. Si cela doit être repris, le
   * moins cher est une paire de chevrons à côté de la légende du tableau, qui porte déjà
   * le mois.
   *
   * Le seuil vertical protège le défilement de la page : un doigt qui monte ou descend
   * plus qu'il ne va à gauche ou à droite ne doit jamais être confisqué au scroll.
   */
  const balayage = useRef<{ x: number; y: number } | null>(null);
  const surDebutBalayage = (e: TouchEvent) => {
    const point = e.touches[0];
    balayage.current = point ? { x: point.clientX, y: point.clientY } : null;
  };
  const surFinBalayage = (e: TouchEvent) => {
    const depart = balayage.current;
    balayage.current = null;
    const point = e.changedTouches[0];
    if (!depart || !point) return;
    const dx = point.clientX - depart.x;
    const dy = point.clientY - depart.y;
    if (Math.abs(dx) < SEUIL_BALAYAGE || Math.abs(dx) < Math.abs(dy)) return;
    changerDeMois(dx < 0 ? 1 : -1);
  };

  const debut = new Date(curseur.getFullYear(), curseur.getMonth(), 1);
  const fin = new Date(curseur.getFullYear(), curseur.getMonth() + 1, 1);

  /**
   * D'où ce dossier tient ses congés scolaires : le calendrier officiel, déduit du code
   * postal de l'école, ou un fichier déposé par un parent depuis « Options ».
   *
   * `staleTime` long : la source ne change qu'à un déménagement, un changement d'école ou
   * un dépôt de fichier, jamais au fil de la navigation entre les mois. L'échec est
   * silencieux — un calendrier sans vacances reste un calendrier juste, alors qu'une erreur
   * en travers de la grille empêcherait de répondre à la question qui amène ici : chez qui
   * dort l'enfant.
   */
  const source = useQuery({
    queryKey: cleSourceConges(caseFileId),
    staleTime: 30 * 60_000,
    retry: false,
    queryFn: () => client.request<SourceCongesView>(`/case-files/${caseFileId}/holiday-source`),
  });

  const periodes = useQuery({
    queryKey: ['occurrences', caseFileId, curseur.getFullYear(), curseur.getMonth()],
    queryFn: () =>
      client.request<Occurrence[]>(`/case-files/${caseFileId}/custody/occurrences`, {
        query: { from: debut.toISOString(), to: fin.toISOString() },
      }),
  });

  const parJour = new Map<string, Occurrence>();
  for (const periode of periodes.data ?? []) {
    const de = new Date(periode.startsAt);
    const a = new Date(periode.endsAt);
    for (let jour = new Date(de); jour < a; jour.setDate(jour.getDate() + 1)) {
      const cle = cleDuJour(jour);
      // Une dérogation prime sur le schéma pour la même journée.
      const existant = parJour.get(cle);
      if (!existant || periode.source === 'exception') parJour.set(cle, periode);
    }
  }

  const nomDuParent = (slot: number): string =>
    parents.find((p) => p.slot === slot)?.isViewer
      ? m.chezVous
      : m.chezAutre(parents.find((p) => p.slot === slot)?.displayName ?? messages.creerDossier.etape2.autreParent);

  /**
   * Les jours de vacances du mois affiché, en clés ISO.
   *
   * Calculé dans le navigateur, quelle que soit la source. Pour le calendrier officiel, les
   * dates sont publiques et immuables : elles voyagent déjà dans `@repere/core`, et les
   * faire redescendre à chaque changement de mois serait un aller-retour pour une
   * information que le client possède — seule la **zone** dépend du dossier. Pour un
   * fichier déposé, les périodes arrivent avec la source, une fois, et le calcul reste ici.
   *
   * `holidayDays` ne lève jamais : un mois hors des données rend un ensemble vide, et
   * l'écran ne montre rien plutôt que d'inventer des vacances.
   */
  const couches = couchesDeConges(
    source.data,
    m,
    cleDuJour(debut),
    cleDuJour(new Date(fin.getTime() - 86_400_000)),
  );

  const semaines = grilleDuMois(curseur.getFullYear(), curseur.getMonth());
  const joursCourts = nomsJoursCourts(locale);

  return (
    <>
      {/* La barre de mois, sur grand écran seulement.
          Elle disait trois fois la même chose sur un téléphone : deux boutons pleine
          hauteur pour « Mois précédent » et « Mois suivant », et entre eux « août 2026 »
          — que la légende du tableau écrit déjà, juste dessous. Le balayage change de mois
          sur téléphone, c'est le geste attendu sur une grille de dates.

          Elle reste sur le rail, où il n'y a pas de balayage et où la place ne manque
          pas. */}
      {rail ? (
        <div className="calendrier-mois">
          <Bouton variante="secondaire" onClick={() => changerDeMois(-1)}>
            <FlecheMois sens="precedent" />
            {m.moisPrecedent}
          </Bouton>
          <strong aria-live="polite">{moisEtAnnee(curseur, locale)}</strong>
          <Bouton variante="secondaire" onClick={() => changerDeMois(1)}>
            {m.moisSuivant}
            <FlecheMois sens="suivant" />
          </Bouton>
        </div>
      ) : null}

      {periodes.isError ? (
        <Alerte titre={m.erreurChargement}>{messageDErreur(periodes.error)}</Alerte>
      ) : null}

      <table
        className="calendrier"
        onTouchStart={surDebutBalayage}
        onTouchEnd={surFinBalayage}
      >
        <caption>{m.titre(moisEtAnnee(curseur, locale))}</caption>
        <thead>
          <tr>
            {joursCourts.map((jour) => (
              <th key={jour} scope="col">
                {jour}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {semaines.map((semaine, index) => (
            <tr key={index}>
              {semaine.map((jour, position) => {
                if (!jour) return <td key={position} />;
                const periode = parJour.get(cleDuJour(jour));
                const slot = periode?.parentSlot ?? null;
                const chezMoi = slot !== null && parents.find((p) => p.slot === slot)?.isViewer;
                const derogation = periode?.source === 'exception';
                // Un échange de jours se nomme, une dérogation ponctuelle se signale.
                // Le point seul ne disait pas ce qui s'était passé, et sur un calendrier
                // de garde c'est justement la question.
                const echange = derogation && periode?.origin === 'day_swap';
                // Les congés scolaires encadrent la case au lieu de la teinter : la
                // couleur de fond dit déjà chez qui dort l'enfant, et lui superposer une
                // seconde teinte rendrait les deux illisibles. Un contour se lit par-dessus
                // n'importe quel fond, et il se cumule sans se confondre.
                //
                // Une couche par enfant quand la fratrie n'a pas les mêmes congés : les
                // anneaux s'emboîtent, du plus intérieur au plus extérieur, chacun de la
                // couleur de son enfant.
                const congesDuJour = couches.filter((couche) => couche.jours.has(cleDuJour(jour)));
                const conge = congesDuJour.length > 0;
                const prenoms = congesDuJour
                  .map((couche) => couche.prenom)
                  .filter((prenom) => prenom !== null);
                const teinte = slot === null ? '' : chezMoi ? 'chez-moi' : 'chez-autre';
                return (
                  <td
                    key={position}
                    className={conge ? `${teinte} conge`.trim() : teinte}
                    style={contoursConges(congesDuJour)}
                  >
                    <span aria-hidden="true">{jour.getDate()}</span>
                    <Invisible>
                      {dateComplete(jour, locale)} :{' '}
                      {slot === null ? m.nonDefini : nomDuParent(slot)}
                      {/* Dit en toutes lettres ce que les contours montrent : une
                          information portée par la seule bordure serait inaccessible, et
                          deux couleurs superposées n'apprennent rien à qui n'y voit pas.
                          Les prénoms, eux, se lisent. */}
                      {conge
                        ? prenoms.length > 0
                          ? m.suffixeCongeEnfants(prenoms.join(', '))
                          : m.suffixeConge
                        : ''}
                      {derogation ? m.suffixeDerogation(libelleDerogation(periode, m)) : ''}
                      {derogation && periode?.reason ? m.suffixeMotif(periode.reason) : ''}
                    </Invisible>
                    {derogation ? (
                      // `title` porte le motif au survol. Il double le texte invisible
                      // ci-dessus plutôt que de le remplacer : `title` n'est pas restitué
                      // de façon fiable par les lecteurs d'écran, et pas du tout au doigt.
                      // Le motif doit être lisible sans souris.
                      <span
                        className={echange ? 'marque-derogation echange' : 'marque-derogation'}
                        title={titreDerogation(periode, m)}
                      >
                        {echange ? m.marqueEchange : '•'}
                      </span>
                    ) : null}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="legende">
        {parents.map((parent) => (
          <li key={parent.slot}>
            <span
              className={`temoin-garde ${parent.isViewer ? 'chez-moi' : 'chez-autre'}`}
              aria-hidden="true"
            />
            {parent.isViewer ? m.chezVous : m.chezAutre(parent.displayName)}
          </li>
        ))}
        <li>
          <span aria-hidden="true">•</span> {m.titreDerogationConvenue}
        </li>
        {/* La légende nomme les deux marqueurs : depuis que l'échange porte son nom, un
            point non expliqué laisserait croire à deux choses différentes. */}
        <li>
          <span className="marque-derogation echange" aria-hidden="true">
            {m.marqueEchange}
          </span>{' '}
          {m.legendeEchange}
        </li>
        {/* La légende des congés n'apparaît que s'il y en a à voir : sur un dossier sans
            code postal d'école ni fichier déposé, elle nommerait un contour qui ne
            s'affiche jamais. Elle nomme la source, parce que c'est ce qui se vérifie —
            une zone se recoupe avec le calendrier officiel, un fichier se remplace. Une
            ligne par enfant quand la fratrie n'a pas les mêmes congés : c'est là que la
            couleur d'un anneau prend un nom. */}
        {couches.map((couche) => (
          <li key={couche.cle}>
            <span
              className="temoin-conge"
              style={couche.teinte ? ({ '--teinte-conge': couche.teinte } as CSSProperties) : undefined}
              aria-hidden="true"
            />{' '}
            {couche.legende}
          </li>
        ))}
        {/* Aucune couche : le calendrier se taisait complètement — ni contour, ni légende,
            ni raison. Sur une grille de garde, des vacances absentes ne se distinguent pas
            d'une panne : on ne sait pas si le produit ignore la zone de l'école ou s'il est
            cassé. La ligne dit ce qui manque et où le corriger.

            Elle attend que la source soit chargée : tant qu'on ne sait pas, on ne dit rien,
            et si la requête a échoué on ne l'accuse pas d'un code postal absent. */}
        {source.data && couches.length === 0 ? (
          <li className="discret">{m.legendeCongesAbsents}</li>
        ) : null}
      </ul>
    </>
  );
}
